import React, { useEffect, useState } from "react";
import {
  FormControl,
  Typography,
  FormLabel,
  Grid,
  SxProps,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { inputStateUpdate } from "../utility";
import { SelectChangeEvent } from "@mui/material";
import theme from "../../../theme";
import useFormStyles from "../../../styles/useFormStyles";
import Loader from "../../ui/Loader";

export type SelectInputFieldProps = {
  label?: string;
  options: string[] | any;
  align: string;
  setState: Function;
  stateKey: string;
  defaultValue: string;
  sx?: SxProps;
  clear?: boolean;
  setClear?: Function;
  required?: boolean;
  multiple?: boolean;
  readOnly?: boolean;
  requiredError?: string;
  searchComponent?: React.ReactNode;
  error?: boolean;
  loading?: boolean;
  onFocus?: (e: any) => void;
  onChange?: (e: SelectChangeEvent<unknown>) => void;
  type?: string;
};

const SelectInputField: React.FC<SelectInputFieldProps> = (
  props: SelectInputFieldProps
) => {
  const {
    label,
    options,
    align,
    setState,
    stateKey,
    defaultValue,
    sx,
    clear,
    setClear,
    required,
    requiredError,
    readOnly,
    searchComponent,
    error,
    loading,
    onFocus,
    type,
  } = props;
  const [selectState, setSelectState] = useState(defaultValue);
  const [touched, setTouched] = useState(false);
  const formStyles = useFormStyles();

  const changeHandler = (e: SelectChangeEvent<unknown>) => {
    if (setClear instanceof Function) setClear(false);
    setSelectState((e.target as HTMLInputElement).value);
    setTouched(true);
  };

  useEffect(() => {
    if (defaultValue) setSelectState(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (clear) {
      setSelectState(defaultValue);
      setTouched(false);
    }
  }, [clear]);

  useEffect(() => {
    // If parent component sets requiredError, mark as touched to show error
    if (requiredError) {
      setTouched(true);
    }
  }, [requiredError]);

  useEffect(() => {
    inputStateUpdate(stateKey, setState, selectState);
  }, [selectState]);

  const readOnlyStyles: React.CSSProperties = {
    backgroundColor: theme.color?.readOnly,
  };
  const isRequiredValid = () => {
    if (required) {
      return !!selectState;
    }
    return true;
  };

  return (
    <Grid width={"100%"}>
      <FormControl size="small" sx={{ width: "100%" }}>
        {label ? (
          <FormLabel sx={{ ...formStyles.formLabel, color: "#252836" }}>
            {label}{" "}
          </FormLabel>
        ) : null}
        {searchComponent ? (
          searchComponent
        ) : (
          <Select
            sx={sx}
            value={selectState}
            onChange={changeHandler}
            disabled={readOnly}
            style={readOnly ? readOnlyStyles : {}}
            error={error || (touched && required && !selectState)}
            onFocus={onFocus}
            onBlur={() => setTouched(true)}
            required={required}
          >
            {options.map((option: any, i: any) => {
              // let optionValue=type==="CountryCode"?option.abbreviation:option
              let optionValue,
                optionLabel = option;
              if (type === "CountryCode") {
                optionLabel = option.code;
                optionValue = option.abbreviation;
              }
              return (
                <MenuItem key={i} value={option}>
                  <Typography textAlign={"left"} variant="body1">
                    {option}
                  </Typography>
                </MenuItem>
              );
            })}
            {loading && (
              <Grid display={"flex"} justifyContent={"center"}>
                <Loader size={30} />
              </Grid>
            )}
          </Select>
        )}
        {touched && required && !selectState && (
          <Typography variant="body2" color="error">
            {requiredError}
          </Typography>
        )}
      </FormControl>
    </Grid>
  );
};

export default SelectInputField;
